import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import { StyleSheet, Animated, Pressable, Platform, useWindowDimensions, View, Easing, ActivityIndicator } from 'react-native';

const KEYBOARD_HEIGHT_ANIM_DURATION = 200;
const KEYBOARD_HEIGHT_ANIM_EASING = Easing.out(Easing.cubic);
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import { useUIStore } from '@/store/ui';
import { usePathname, useRouter } from 'expo-router';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MODAL_COMPONENTS, isRegisteredModal, preloadCriticalModals } from '@/components/Modal/modalRegistry';

const backdropOpacityMap = {
  "sepia": 0.6,
  "muted": 0.75,
  "night": 0.85,
  "smoke": 0.8,
}

const styles = StyleSheet.create({
  modalWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 0,
    zIndex: 10002,
    ...Platform.select({
      macos: { cursor: 'default' },
      default: {},
    }),
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 10003,
  },
  content: {
    zIndex: 10004,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.2), 0 4px 24px rgba(0, 0, 0, 0.1)',
        WebkitBoxShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.2), 0 4px 24px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 24,
        elevation: 8,
      },
    }),
    position: 'relative',
  },
  suspenseFallback: {
    flex: 1,
    minHeight: 140,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

/**
 * Shown inside the modal shell while a lazy (non-preloaded) modal chunk loads,
 * so the user sees a centered spinner instead of a blank white modal.
 */
function ModalSuspenseFallback() {
  const { colors } = useTheme();
  return (
    <View style={styles.suspenseFallback}>
      <ActivityIndicator size="small" color={colors.text_color_secondary} />
    </View>
  );
}

function ModalBase({
  isOpen,
  onClose,
  onDismissComplete,
  children,
  width = 400,
  height = 400,
  background = 'primary',
  heightMobile = '75%',
}) {
  const { colors, theme } = useTheme();
  const { width: rawWindowWidth, height: rawWindowHeight } = useWindowDimensions();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const isMobileWebEarly = Platform.OS === 'web' && isXS;
  // Mobile web: freeze the width while open so URL-bar/keyboard resize events can't
  // reflow the sheet horizontally. Height must stay live — freezing it sizes the
  // sheet to whatever the viewport was when it opened (often shrunk by the on-screen
  // keyboard), leaving it too short and floating above the bottom edge.
  const stableWindowWidth = useRef(rawWindowWidth);
  if (!isMobileWebEarly || !isOpen) {
    stableWindowWidth.current = rawWindowWidth;
  }
  const windowWidth = isMobileWebEarly && isOpen ? stableWindowWidth.current : rawWindowWidth;
  const increaseBorderContrast = useUIStore(state => state.increaseBorderContrast);
  const modalRef = React.useRef(null);
  const [animation] = React.useState(new Animated.Value(0));
  const [contentAnimation] = React.useState(new Animated.Value(0));
  const [backgroundOverride, setBackgroundOverride] = useState(null);
  const [dimensionsOverride, setDimensionsOverride] = useState(null);
  const [macViewport, setMacViewport] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  // Child modals (e.g. cancellation-save) can override size per step; reset when
  // the declared modal dimensions change so the next modal doesn't inherit them.
  React.useEffect(() => {
    setDimensionsOverride(null);
  }, [width, height, heightMobile]);
  const wasOpenRef = useRef(false);
  const isMobileOrXS = isXS;
  const isNative = Platform.OS !== 'web';
  const isMobileWeb = Platform.OS === 'web' && isXS;
  const composerKeyboardHeight = useComposerKeyboardHeight();
  const keyboardVisible = isNative && isXS && isOpen && composerKeyboardHeight > 0;
  const keyboardExpandAnim = useRef(new Animated.Value(0)).current; // 0 = normal, 1 = expanded

  // Pass the handler to children (kept for modals that use onBackgroundChange / onDimensionsChange)
  const childrenWithProps = useMemo(() => React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onBackgroundChange: setBackgroundOverride,
        onDimensionsChange: setDimensionsOverride,
      });
    }
    return child;
  }), [children]);

  // Allow child modals to dynamically override width/height (e.g. per A/B test variant)
  const effectiveWidth = dimensionsOverride?.width ?? width;
  const effectiveHeight = dimensionsOverride?.height ?? height;
  const effectiveHeightMobile = dimensionsOverride?.heightMobile ?? heightMobile;

  const safeWindowHeight = Math.max(rawWindowHeight || 0, 400);
  const padding = 48;
  const minTopInset = isMobileWebEarly ? 20 : 60; // Mobile web can sit closer to the top so tall sheets stay readable

  // Desktop: use height prop capped to viewport. Mobile/isXS: use resolved height from heightMobile logic.
  const maxDesktopHeight = safeWindowHeight - padding;
  const desktopModalHeight = typeof effectiveHeight === 'number' ? Math.min(effectiveHeight, maxDesktopHeight) : maxDesktopHeight;

  // Resolve modal height (mobile/isXS only): prefer heightMobile (string '%', number, or array [number, '%']), else height prop.
  // When height is 'auto', use a sensible default so we don't get NaN.
  // Cap so modal top is always at least minTopInset from the top.
  // Add bottom safe area inset so modal fills to the physical device bottom (no gap).
  const resolvedModalHeight = useMemo(() => {
    const maxByTop = safeWindowHeight - minTopInset;
    const defaultHeight = safeWindowHeight - padding;
    const resolvedH = typeof effectiveHeight === 'number' ? effectiveHeight : defaultHeight;
    let h;
    if (effectiveHeightMobile == null || effectiveHeightMobile === '') {
      h = Math.min(resolvedH, defaultHeight);
    } else if (Array.isArray(effectiveHeightMobile)) {
      const first = effectiveHeightMobile[0];
      const second = effectiveHeightMobile[1];
      if (typeof first === 'number') {
        h = Math.min(first, defaultHeight);
        if (typeof second === 'string' && second.endsWith('%')) {
          const pct = parseInt(second, 10) || 75;
          const maxH = (pct / 100) * safeWindowHeight;
          h = Math.min(h, maxH);
        }
      } else if (typeof first === 'string' && first.endsWith('%')) {
        const pct = parseInt(first, 10) || 75;
        h = Math.min((pct / 100) * safeWindowHeight, defaultHeight);
      } else {
        h = Math.min(resolvedH, defaultHeight);
      }
    } else if (typeof effectiveHeightMobile === 'string' && effectiveHeightMobile.endsWith('%')) {
      const pct = parseInt(effectiveHeightMobile, 10) || 75;
      h = Math.min((pct / 100) * safeWindowHeight, defaultHeight);
    } else if (typeof effectiveHeightMobile === 'number') {
      h = Math.min(effectiveHeightMobile, defaultHeight);
    } else {
      h = Math.min(resolvedH, defaultHeight);
    }
    // Soft-cap so fixed heightMobile values (850, 1200, …) can't dominate
    // short iPhone Safari viewports. 96% leaves a sliver of the page visible.
    h = Math.min(h, Math.round(0.96 * safeWindowHeight));
    // Extend into bottom safe area so modal touches device bottom (no gap), but never exceed the top gap
    const maxAllowed = safeWindowHeight - minTopInset - insets.top;
    return Math.min(h, maxAllowed);
  }, [effectiveHeightMobile, effectiveHeight, safeWindowHeight, insets.top, minTopInset]);

  // safeWindowHeight has a 400px floor, so clamp mobile web against the true
  // viewport as well — otherwise a very short viewport gets a sheet whose footer
  // sits below the bottom edge.
  const mobileWebModalHeight = Math.min(resolvedModalHeight, Math.max(rawWindowHeight || 0, 240) - minTopInset);

  // When keyboard is open on native + isXS, extend modal up but respect top safe area and 100px top gap; reset when keyboard closes.
  const fullHeightWithGap = safeWindowHeight - insets.top - minTopInset;
  const effectiveModalHeight = (keyboardVisible && isNative && isXS) ? fullHeightWithGap : resolvedModalHeight;
  const bottomAlignOffsetYNormal = safeWindowHeight / 2 - resolvedModalHeight;
  const bottomAlignOffsetYExpanded = safeWindowHeight / 2 - fullHeightWithGap;
  // Desktop (web and !isXS): center vertically using desktop height. Native/isXS: bottom-aligned.
  const centerOffsetY = -(isMobileOrXS ? resolvedModalHeight : desktopModalHeight) / 2;

  React.useEffect(() => {
    if (!isOpen && (isNative && isXS)) {
      keyboardExpandAnim.setValue(0);
    }
  }, [isOpen, isNative, isXS, keyboardExpandAnim]);

  React.useEffect(() => {
    if (!isNative || !isXS) return;
    Animated.timing(keyboardExpandAnim, {
      toValue: keyboardVisible ? 1 : 0,
      duration: KEYBOARD_HEIGHT_ANIM_DURATION,
      easing: KEYBOARD_HEIGHT_ANIM_EASING,
      useNativeDriver: false,
    }).start();
  }, [keyboardVisible, isNative, isXS, keyboardExpandAnim]);

  React.useEffect(() => {
    if (!isMobileWeb || !isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, [isMobileWeb, isOpen]);

  React.useEffect(() => {
    if (!isMobileWeb || !isOpen) return;
    const vv = typeof window !== 'undefined' && window.visualViewport;
    if (!vv) return;
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-modal-keyboard', 'true');
    document.head.appendChild(styleEl);
    const onResize = () => {
      const viewportH = vv.height;
      const fullH = window.innerHeight;
      const keyboardOpen = fullH - viewportH > 100;
      if (keyboardOpen) {
        const modalH = Math.min(viewportH - 10, mobileWebModalHeight);
        const topPos = vv.offsetTop + 5;
        styleEl.textContent = `
          [data-mobile-web-modal="true"] {
            height: ${modalH}px !important;
            max-height: ${modalH}px !important;
            top: ${topPos}px !important;
            transform: translateX(-50%) !important;
          }
        `;
      } else {
        styleEl.textContent = '';
      }
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
      window.removeEventListener('resize', onResize);
      styleEl.remove();
    };
  }, [isMobileWeb, isOpen, mobileWebModalHeight]);

  // Overlay can use native driver; content must use JS driver so it can combine with keyboard height animation
  const overlayNativeDriver = Platform.OS !== 'web';
  const contentNativeDriver = false;
  // Animated height and offset for smooth keyboard expand/collapse (native + isXS only). Desktop: just use height prop.
  const animatedHeight = (isNative && isXS)
    ? keyboardExpandAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [resolvedModalHeight, fullHeightWithGap],
      })
    : isMobileWeb
      ? mobileWebModalHeight
      : (isMobileOrXS ? effectiveModalHeight : desktopModalHeight);
  const animatedOffsetY = (isNative && isXS)
    ? keyboardExpandAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [bottomAlignOffsetYNormal, bottomAlignOffsetYExpanded],
      })
    : bottomAlignOffsetYNormal;
  // Full width on mobile/isXS, else respect width prop with padding
  const contentWidth = isMobileOrXS ? windowWidth : Math.min(effectiveWidth, windowWidth - padding);

  React.useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      setIsExiting(false);
      Animated.timing(animation, {
        toValue: 1,
        duration: 280,
        useNativeDriver: overlayNativeDriver,
      }).start();

      Animated.timing(contentAnimation, {
        toValue: 1,
        duration: 320,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: contentNativeDriver,
      }).start();

      if (modalRef.current) {
        modalRef.current.focus();
      }

      const handleEscapeKey = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      if (Platform.OS === 'web') {
        document.addEventListener('keydown', handleEscapeKey);
      }

      return () => {
        if (Platform.OS === 'web') {
          document.removeEventListener('keydown', handleEscapeKey);
        }
      };
    }
  }, [isOpen]);

  const exitDuration = isMobileOrXS ? 300 : 0;
  const overlayExitDuration = isMobileOrXS ? 260 : 0;

  React.useLayoutEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
      setIsExiting(true);
      Animated.timing(contentAnimation, {
        toValue: 0,
        duration: exitDuration,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: contentNativeDriver,
      }).start(({ finished }) => {
        if (finished) {
          onDismissComplete?.();
          setIsExiting(false);
        }
      });
      Animated.timing(animation, {
        toValue: 0,
        duration: overlayExitDuration,
        useNativeDriver: overlayNativeDriver,
      }).start();
    }
  }, [isOpen]);

  const isVisible = isOpen || isExiting;
  if (!isVisible) return null;

  const toOpacity = backdropOpacityMap[theme] || 0.5;
  const isBlack = theme === "black";
  const backgroundColor = isBlack ? "rgb(40, 40, 40)" : "rgb(0, 0, 0)";
  const modalBackgroundColor = backgroundOverride ?? (background === 'block' ? colors.solid_color_block : colors.solid_color_primary);

  // Mobile/native/isXS: bottom-sheet style (top radius only). Desktop: all corners curved.
  const contentBorderRadius = isMobileOrXS
    ? {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }
    : {
        borderRadius: 16,
      };

  // Modal bottom aligns with screen bottom (not centered). Left/right centered via translateX.
  // On mobile/isXS: slide up from off-screen to bottom-aligned; on close slides back down.
  // When keyboard expand is animated, combine enter animation with animated offset.
  const mobileTranslateY = (isNative && isXS)
    ? Animated.add(
        Animated.multiply(Animated.subtract(1, contentAnimation), safeWindowHeight),
        Animated.multiply(contentAnimation, animatedOffsetY)
      )
    : contentAnimation.interpolate({
        inputRange: [0, 1],
        // Mobile web anchors to the bottom edge, so the sheet only has to travel
        // its own height to be fully off-screen.
        outputRange: isMobileWeb
          ? [mobileWebModalHeight, 0]
          : [safeWindowHeight, bottomAlignOffsetYNormal],
      });
  const contentTransform = isMobileOrXS
    ? [
        { translateX: -(contentWidth / 2) },
        { translateY: mobileTranslateY },
      ]
    : [
        { translateX: -(contentWidth / 2) },
        { translateY: centerOffsetY },
        {
          scale: contentAnimation.interpolate({
            inputRange: [0, 1],
            outputRange: [0.96, 1],
          }),
        },
      ];

  const isMacOS = Platform.OS === 'macos';

  if (isMacOS) {
    const macOSBackdropOpacity = 0;
    const macScaleTransform = [{
      scale: contentAnimation.interpolate({
        inputRange: [0, 1],
        outputRange: [0.96, 1],
      }),
    }];

    return (
      <View
        onLayout={event => {
          const { width, height } = event.nativeEvent.layout;
          setMacViewport(previous => previous?.width === width && previous?.height === height ? previous : { width, height });
        }}
        style={[StyleSheet.absoluteFill, styles.modalWrapper, { cursor: 'default' }]}
      >
        <Animated.View
          pointerEvents="none"
          style={[
            styles.overlay,
            {
              backgroundColor,
              opacity: animation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, macOSBackdropOpacity],
              }),
            },
          ]}
        />
        <Pressable
          focusable={false}
          style={[StyleSheet.absoluteFill, { zIndex: 10002, alignItems: 'center', justifyContent: 'center', cursor: 'default' }]}
          onPress={onClose}
        >
          <Animated.View
            ref={modalRef}
            style={[
              styles.content,
              {
                backgroundColor: modalBackgroundColor,
                width: Math.min(typeof contentWidth === 'number' ? contentWidth : windowWidth, (macViewport?.width ?? windowWidth) * 0.92),
                height: Math.min(desktopModalHeight, (macViewport?.height ?? rawWindowHeight) * 0.92),
                transform: macScaleTransform,
                borderRadius: 16,
                ...(increaseBorderContrast && {
                  borderWidth: 1,
                  borderColor: colors.border_color,
                }),
              },
            ]}
          >
            <View style={{ flex: 1, flexBasis: 0, minHeight: 0 }} onStartShouldSetResponder={() => true}>
              {childrenWithProps}
            </View>
          </Animated.View>
        </Pressable>
      </View>
    );
  }

  const contentView = (
    <Animated.View
      ref={modalRef}
      tabIndex={-1}
      {...(isMobileWeb && { dataSet: { mobileWebModal: 'true' } })}
      style={[
        styles.content,
        {
          backgroundColor: modalBackgroundColor,
          width: contentWidth,
          maxHeight: animatedHeight,
          height: animatedHeight,
          outlineStyle: 'none',
          position: 'absolute',
          left: '50%',
          ...(isMobileWeb ? { bottom: 0 } : { top: '50%' }),
          transform: contentTransform,
          ...contentBorderRadius,
          ...(increaseBorderContrast && {
            borderWidth: 1,
            borderColor: colors.border_color,
          }),
        },
      ]}
    >
      {/* flex + minHeight:0 so ModalLayout's ScrollView can shrink and scroll
          inside the fixed-height shell on mobile web (overflow:hidden). */}
      <View style={{ flex: 1, minHeight: 0, width: '100%' }}>
        {childrenWithProps}
      </View>
    </Animated.View>
  );

  return (
    <View style={[StyleSheet.absoluteFill, styles.modalWrapper, isMobileWeb && { position: 'fixed' }]}>
      <Animated.View
        style={[
          styles.overlay,
          {
            backgroundColor,
            opacity: animation.interpolate({
              inputRange: [0, 1],
              outputRange: [0, toOpacity],
            }),
          },
        ]}
      >
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
        />
      </Animated.View>
      {contentView}
    </View>
  );
}

export default function ModalRoot() {
  const isOpen = useModalStore(state => state.isOpen);
  const modalName = useModalStore(state => state.modalName);
  const modalProps = useModalStore(state => state.modalProps);
  const { key: modalKey, forceTheme, ...componentProps } = modalProps || {};
  const options = useModalStore(state => state.options);
  const { closeModal, clearModal, openModal } = useModalStore(state => state.actions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isHeldForReview = useAuthStore(state => state.user?.flags?.heldForReview === true);
  const shownReviewHoldRef = React.useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const previousPathnameRef = React.useRef(pathname);

  // Preload login/signup/pricing chunks as soon as ModalRoot mounts (idempotent).
  React.useEffect(() => {
    preloadCriticalModals().catch(() => {});
  }, []);

  // Show the review-hold lockdown once per held session so the user knows
  // to email ai@gab.com. Dismissing is allowed; send/checkout reopen it.
  React.useEffect(() => {
    if (!isHeldForReview) {
      shownReviewHoldRef.current = false;
      return;
    }
    if (shownReviewHoldRef.current) return;
    shownReviewHoldRef.current = true;
    openModal('AccountReviewHoldModal', { source: 'session' });
  }, [isHeldForReview, openModal]);

  // iOS: intercept PricingModal open requests and redirect to the native paywall
  // instead of rendering the modal (which would crash due to cross-group navigation).
  React.useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!isOpen || modalName !== 'PricingModal') return;
    if (isGhost || isPlus) return;
    closeModal();
    router.push('/(auth)/paywall');
  }, [isOpen, modalName, isGhost, isPlus, closeModal, router]);

  // Close modal when the route path actually changes while open.
  React.useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const didPathChange = previousPathname !== pathname;
    previousPathnameRef.current = pathname;
    if (!isOpen || !didPathChange) return;
    closeModal();
  }, [pathname, isOpen, closeModal]);

  const ModalComponent = modalName ? MODAL_COMPONENTS[modalName] : null;
  const [resolvedModalOptions, setResolvedModalOptions] = useState({});

  // On iOS, don't render PricingModal at all (handled by redirect above)
  const shouldSkipRender = Platform.OS === 'ios' && modalName === 'PricingModal' && !isGhost && !isPlus;

  React.useEffect(() => {
    if (!isOpen || !modalName || isRegisteredModal(modalName)) return;
    console.error(`[ModalRoot] Unknown modal "${modalName}" requested. Closing modal to avoid blank state.`);
    closeModal();
  }, [isOpen, modalName, closeModal]);

  // If the chunk is already loaded (critical modals are preloaded on mount),
  // render the resolved component DIRECTLY — no React.lazy/Suspense, so no
  // one-frame blank/white flash. Derived during render so it's correct on the
  // very first render, with no dependence on effect timing.
  const LoadedComponent = ModalComponent?.getComponent?.() || null;

  // Resolve modalOptions from the (possibly preloaded) chunk. When a modal
  // isn't loaded yet, the preload().then below resolves options and triggers a
  // re-render, at which point LoadedComponent above becomes available.
  useLayoutEffect(() => {
    if (!modalName || !ModalComponent) {
      setResolvedModalOptions({});
      return;
    }

    const cached = ModalComponent.getModalOptions?.();
    if (cached) {
      setResolvedModalOptions(cached);
      return;
    }

    let cancelled = false;
    ModalComponent.preload?.().then(() => {
      if (!cancelled) {
        setResolvedModalOptions(ModalComponent.getModalOptions?.() ?? {});
      }
    }).catch((error) => {
      console.error(`[ModalRoot] Failed to load modal "${modalName}":`, error);
      if (!cancelled) closeModal();
    });

    return () => {
      cancelled = true;
    };
  }, [modalName, ModalComponent, closeModal]);

  const mergedOptions = ModalComponent ? {
    ...resolvedModalOptions,
    ...options,
  } : {};

  const modal = (
    ModalComponent && !shouldSkipRender ? (
      <ModalBase
        key={modalName}
        isOpen={isOpen}
        onClose={closeModal}
        onDismissComplete={clearModal}
        {...mergedOptions}
      >
        {LoadedComponent ? (
          <LoadedComponent key={modalKey} {...componentProps} onClose={closeModal} />
        ) : (
          <React.Suspense fallback={<ModalSuspenseFallback />}>
            <ModalComponent key={modalKey} {...componentProps} onClose={closeModal} />
          </React.Suspense>
        )}
      </ModalBase>
    ) : null
  );

  // Onboarding dialogs share its light palette even though they render here.
  return forceTheme && modal
    ? <ThemeProvider forceTheme={forceTheme} scoped>{modal}</ThemeProvider>
    : modal;
}
